import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { ChatMessage, ChartData } from '../types';
import { exportPDF } from '../utils/api';

interface ExportPDFButtonProps {
  messages: ChatMessage[];
  sessionId: string;
}

// CSS variables from ChartWidget that chart SVGs depend on
const CHART_CSS = `
<style>
  :root {
    --color-background-primary: #ffffff;
    --color-background-secondary: #f5f5f5;
    --color-background-tertiary: #fafafa;
    --color-text-primary: #1a1a1a;
    --color-text-secondary: #666666;
    --color-text-tertiary: #999999;
    --color-border-tertiary: rgba(0, 0, 0, 0.08);
    --color-border-secondary: rgba(0, 0, 0, 0.15);
    --color-border-primary: rgba(0, 0, 0, 0.2);
    --color-text-info: #185fa5;
    --color-text-success: #3b6d11;
    --color-text-warning: #854f0b;
    --color-text-danger: #a32d2d;
    --color-background-info: #e6f1fb;
    --color-background-success: #eaf3de;
    --color-background-warning: #faeeda;
    --color-background-danger: #fcebeb;
    --border-radius-md: 8px;
    --border-radius-lg: 12px;
    --border-radius-xl: 16px;
    --font-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    --font-serif: Georgia, 'Times New Roman', serif;
    --font-mono: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  }
  .c-purple { fill: #EEEDFE; stroke: #534AB7; }
  .c-teal { fill: #E1F5EE; stroke: #0F6E56; }
  .c-coral { fill: #FAECE7; stroke: #993C1D; }
  .c-pink { fill: #FBEAF0; stroke: #993556; }
  .c-gray { fill: #F1EFE8; stroke: #5F5E5A; }
  .c-blue { fill: #E6F1FB; stroke: #185FA5; }
  .c-green { fill: #EAF3DE; stroke: #3B6D11; }
  .c-amber { fill: #FAEEDA; stroke: #854F0B; }
  .c-red { fill: #FCEBEB; stroke: #A32D2D; }
  .t { font-family: var(--font-sans); font-size: 14px; font-weight: 400; fill: var(--color-text-primary); }
  .ts { font-family: var(--font-sans); font-size: 12px; font-weight: 400; fill: var(--color-text-secondary); }
  .th { font-family: var(--font-sans); font-size: 14px; font-weight: 500; fill: var(--color-text-primary); }
  .c-purple .t, .c-purple .th { fill: #3C3489; }
  .c-purple .ts { fill: #534AB7; }
  .c-teal .t, .c-teal .th { fill: #085041; }
  .c-teal .ts { fill: #0F6E56; }
  .c-coral .t, .c-coral .th { fill: #712B13; }
  .c-coral .ts { fill: #993C1D; }
  .c-pink .t, .c-pink .th { fill: #72243E; }
  .c-pink .ts { fill: #993556; }
  .c-gray .t, .c-gray .th { fill: #444441; }
  .c-gray .ts { fill: #5F5E5A; }
  .c-blue .t, .c-blue .th { fill: #0C447C; }
  .c-blue .ts { fill: #185FA5; }
  .c-green .t, .c-green .th { fill: #27500A; }
  .c-green .ts { fill: #3B6D11; }
  .c-amber .t, .c-amber .th { fill: #633806; }
  .c-amber .ts { fill: #854F0B; }
  .c-red .t, .c-red .th { fill: #791F1F; }
  .c-red .ts { fill: #A32D2D; }
  .box { fill: var(--color-background-secondary); stroke: var(--color-border-tertiary); }
  .arr { stroke: var(--color-text-tertiary); stroke-width: 1.5; fill: none; }
  .leader { stroke: var(--color-text-tertiary); stroke-width: 0.5; stroke-dasharray: 2,2; }
  .is_container { width: 100%; display: block; }
</style>
`;

async function captureChart(chart: ChartData): Promise<string | null> {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '800px';
  container.innerHTML = CHART_CSS + `<div class="is_container">${chart.code}</div>`;
  document.body.appendChild(container);

  try {
    // Wait for SVGs to render
    await new Promise(resolve => setTimeout(resolve, 200));
    const chartEl = container.querySelector('.is_container') as HTMLElement;
    const canvas = await html2canvas(chartEl, {
      backgroundColor: '#ffffff',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  } catch (err) {
    console.error(`Failed to capture chart "${chart.title}":`, err);
    return null;
  } finally {
    document.body.removeChild(container);
  }
}

export const ExportPDFButton: React.FC<ExportPDFButtonProps> = ({ messages, sessionId }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const assistantMessages = messages.filter(m => m.role === 'assistant');
      const title = assistantMessages[0]?.content?.split('\n')[0]?.replace(/^#+\s*/, '') || '研究报告';

      const sections: Array<{ type: 'markdown' | 'chart_image'; content: string; title?: string }> = [];

      for (const msg of assistantMessages) {
        if (msg.content) {
          sections.push({ type: 'markdown', content: msg.content });
        }
        if (msg.charts) {
          for (const chart of msg.charts) {
            const image = await captureChart(chart);
            if (image) {
              sections.push({ type: 'chart_image', title: chart.title, content: image });
            } else {
              toast.warning(`图表 "${chart.title}" 截图失败，已跳过`);
            }
          }
        }
      }

      const blob = await exportPDF(sessionId, { title, sections });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDone(true);
      toast.success('PDF 已下载');
      setTimeout(() => setDone(false), 2000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'PDF 导出失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      className="export-pdf-button"
      onClick={handleExport}
      disabled={loading}
    >
      {loading ? (
        <>
          <span className="loading-spinner" />
          生成中...
        </>
      ) : done ? (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          已下载
        </>
      ) : (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          导出 PDF
        </>
      )}
    </button>
  );
};
